import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Linkedin, Instagram, Facebook, Building2, Globe, MapPin, Users, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function slugify(str = '') {
  return str.toLowerCase().trim()
    .replace(/ğ/g,'g').replace(/ü/g,'u').replace(/ş/g,'s')
    .replace(/ı/g,'i').replace(/ö/g,'o').replace(/ç/g,'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function TeamMemberPage() {
  const { slug, memberName } = useParams();
  const navigate = useNavigate();
  const [contractor, setContractor] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/api/public/contractor/${slug}`);
        const data = res.data;
        const org = data.org || {};
        const profile = data.profile || {};
        const team = profile.team || [];

        const resolveUrl = (path) => {
          if (!path) return null;
          if (path.startsWith('http')) return path;
          return `${API}/api/contractor/profile/assets/${path}`;
        };

        const enrichedTeam = team.map(m => ({
          ...m,
          avatar: resolveUrl(m.avatar_url || m.avatar),
        }));

        const contractorInfo = {
          name: org.display_name || profile.name,
          location: [org.city, org.country].filter(Boolean).join(', '),
          logo_url: resolveUrl(profile.logo_url),
          team: enrichedTeam,
        };

        setContractor(contractorInfo);
        const found = enrichedTeam.find(m => slugify(m.name) === memberName);
        setMember(found || enrichedTeam[0] || null);
      } catch (err) {
        console.error('TeamMemberPage error:', err);
        navigate(`/contractor/${slug}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, memberName, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500">Team member not found.</p>
        <Link to={`/contractor/${slug}`} className="text-orange-600 font-semibold hover:underline">
          Go Back
        </Link>
      </div>
    );
  }

  const initials = (member.name || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const otherMembers = (contractor?.team || []).filter(m => slugify(m.name) !== memberName);

  return (
    <div className="min-h-screen bg-[#f7f7f5]">
      {/* ─── Top Bar ─── */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-100 px-4 sm:px-8 h-14 flex items-center justify-between">
        <button
          onClick={() => navigate(`/contractor/${slug}#team`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {contractor?.name || 'Geri Dön'}
        </button>
        <Link to="/" className="text-sm font-black tracking-tight">
          <span className="text-orange-500">EXPO</span>
          <span className="text-gray-800"> STAND</span>
        </Link>
      </header>

      {/* ─── Hero ─── */}
      <div className="relative bg-[#0d1f17] overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #16a34a 0%, transparent 50%), radial-gradient(circle at 80% 20%, #f97316 0%, transparent 40%)' }} />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-8 py-16 flex flex-col sm:flex-row items-center gap-10">
          {/* Photo */}
          <div className="flex-shrink-0">
            {member.avatar
              ? <img src={member.avatar} alt={member.name}
                  className="w-40 h-40 sm:w-52 sm:h-52 rounded-3xl object-cover object-center shadow-2xl ring-4 ring-white/10" />
              : <div className="w-40 h-40 sm:w-52 sm:h-52 rounded-3xl flex items-center justify-center text-5xl sm:text-6xl font-black text-white shadow-2xl ring-4 ring-white/10"
                  style={{ background: 'linear-gradient(135deg, #16a34a, #f97316)' }}>
                  {initials}
                </div>
            }
          </div>

          {/* Info */}
          <div className="text-center sm:text-left">
            <p className="text-orange-400 text-sm font-semibold tracking-widest uppercase mb-2">{member.role}</p>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-3 leading-tight">{member.name}</h1>

            {/* Company badge */}
            {contractor?.name && (
              <Link to={`/contractor/${slug}`}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full px-4 py-1.5 text-white/80 text-sm font-medium mb-6">
                <Building2 className="w-3.5 h-3.5" />
                {contractor.name}
                <ExternalLink className="w-3 h-3" />
              </Link>
            )}

            {/* Social links */}
            {(member.linkedin || member.instagram || member.facebook) && (
              <div className="flex items-center justify-center sm:justify-start gap-3">
                {member.linkedin && (
                  <a href={member.linkedin} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 bg-[#0A66C2] hover:bg-[#0A66C2]/80 transition-colors text-white text-sm font-semibold px-4 py-2 rounded-xl">
                    <Linkedin className="w-4 h-4" /> LinkedIn
                  </a>
                )}
                {member.instagram && (
                  <a href={member.instagram} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:opacity-80 transition-opacity"
                    style={{ background: 'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)' }}>
                    <Instagram className="w-4 h-4" /> Instagram
                  </a>
                )}
                {member.facebook && (
                  <a href={member.facebook} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 bg-[#1877F2] hover:bg-[#1877F2]/80 transition-colors text-white text-sm font-semibold px-4 py-2 rounded-xl">
                    <Facebook className="w-4 h-4" /> Facebook
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Content ─── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Bio */}
        <div className="lg:col-span-2 space-y-6">
          {member.bio && (
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-orange-500 rounded-full inline-block" />
                About
              </h2>
              <p className="text-gray-600 leading-relaxed text-[15px] whitespace-pre-wrap">{member.bio}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Company info */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Company</h3>
            <Link to={`/contractor/${slug}`}
              className="flex items-center gap-3 group">
              <div>
                <p className="font-bold text-gray-900 group-hover:text-orange-600 transition-colors text-sm">{contractor?.name}</p>
                {contractor?.location && (
                  <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" /> {contractor.location}
                  </p>
                )}
              </div>
            </Link>
          </div>

          {/* Other team members */}
          {otherMembers.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" /> Other Team Members
              </h3>
              <div className="space-y-3">
                {otherMembers.map((m, i) => (
                  <Link key={i}
                    to={`/contractor/${slug}/team/${slugify(m.name)}`}
                    className="flex items-center gap-3 group">
                    {m.avatar
                      ? <img src={m.avatar} alt={m.name} className="w-10 h-10 rounded-xl object-cover object-center flex-shrink-0" />
                      : <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                          style={{ background: `hsl(${(i * 80 + 140) % 360},45%,45%)` }}>
                          {m.name.split(' ').map(n => n[0]).join('').slice(0,2)}
                        </div>
                    }
                    <div>
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">{m.name}</p>
                      <p className="text-xs text-gray-400">{m.role}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
